import {getCurrentUserIdToken} from "../../utils/user";
import {getSingleAttributeValue} from "./getSingleAttributeValue";
import {IAuthService} from "../../platform/services/auth/AuthService";

import {IGraphQLSubscriptionClient} from "./IGraphQLSubscriptionClient";


// Define the message structure
type Message = {
    id: string;
    type: string;
    payload: {
        data: any
    };
};

function encodeAppSyncCredentials(host: string, token: string) {
    return window.btoa(JSON.stringify({
        host: host,
        Authorization: token,
    }));
}

export interface SubscriptionNotation {
    id: string;
    query: string;
    cb: (item: any) => void;
    params?: Object;
}

export class AppSyncWebSocketClient implements IGraphQLSubscriptionClient {
    private ws: WebSocket | null = null;
    private reconnectInterval: number = 500;
    private reconnectAttempts: number = 0;
    private reconnectTimeout: number | null = null;
    private isConnecting = false;
    private isConnectionAcknowledged = false;

    private readonly url: string;
    private readonly tenantId: string;
    private readonly apiHost: string;
    private readonly authService: IAuthService;

    private readonly subscriptionsMap: Map<string, SubscriptionNotation> = new Map();

    private _onOnline = () => this.start();
    private _onOffline = () => { /* noop */ };
    private _onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.reconnectAttempts = 0;
            this.scheduleReconnect();
        }
    };

    constructor(
        url: string,
        apiHost: string,
        authService: IAuthService,
        tenantId: string,
    ) {
        this.url = url;
        this.apiHost = apiHost;
        this.authService = authService;
        this.tenantId = tenantId;

        window.addEventListener('online', this._onOnline);
        window.addEventListener('offline', this._onOffline);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    private safeSend(payload: any) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(payload));
            return true;
        }
        this.scheduleReconnect();
        return false;
    }

    // Initialize the WebSocket connection
    public async connect() {
        const token = await getCurrentUserIdToken(this.authService);
        if (!token) {
            throw new Error('no valid auth token');
        }

        const payload = window.btoa(JSON.stringify({}));
        this.ws = new WebSocket(
            `${this.url}?header=${encodeAppSyncCredentials(this.apiHost, token)}&payload=${payload}`,
            'graphql-ws'
        );

        this.ws.addEventListener('open', () => {
            this.reconnectAttempts = 0;
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }

            // Send connection_init message
            this.safeSend({
                type: 'connection_init',
            });
        });

        this.ws.addEventListener('message', (event) => {
            const message: Message = JSON.parse(event.data);

            switch (message.type) {
                case "connection_ack":
                    this.isConnectionAcknowledged = true;
                    this.initSubscriptions();
                    break;
                case "start_ack":
                    break;
                case "error":
                    console.error(message);
                    break;
                case "data":
                    this.propagateMessage(message.id, getSingleAttributeValue(message.payload.data))
                    break;
            }
        });

        this.ws.addEventListener('close', () => {
            this.scheduleReconnect();
        });

        this.ws.addEventListener('error', () => {
            this.scheduleReconnect();
        });
    }

    public async registerSubscription(id: string, query: string, cb: (item: any) => void, queryParams = {}) {
        if (this.subscriptionsMap.has(id)) {
            const previousSubscription = this.subscriptionsMap.get(id)!;
            this.subscriptionsMap.set(id, {
                ...previousSubscription,
                cb: cb,
            });
            return;
        }
        this.subscriptionsMap.set(id, {
            id, query, cb,
            params: queryParams,
        });
        if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isConnectionAcknowledged) {
            const token = await getCurrentUserIdToken(this.authService);
            if (!token) {
                throw new Error('no valid auth token');
            }
            this.subscribe(id, query, token!, queryParams);
        }
    }

    public cancelSubscription(id: string) {
        this.subscriptionsMap.delete(id);
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.unsubscribe(id,);
        }
    }

    private async initSubscriptions() {
        const token = await getCurrentUserIdToken(this.authService);
        if (!token) {
            throw new Error('no valid auth token');
        }

        for (const subscriptionNotation of this.subscriptionsMap.values()) {
            this.subscribe(subscriptionNotation.id, subscriptionNotation.query, token!, subscriptionNotation.params);
        }
    }

    private propagateMessage(id: string, data: string) {
        const subscription = this.subscriptionsMap.get(id);
        if (subscription) {
            subscription.cb(data);
        }
    }

    private subscribe(id: string, query: string, token: string, queryParams: Record<string, any> = {}) {
        const variables: Record<string, any> = {
            ownerId: this.tenantId,
        }

        if (Object.keys(queryParams).length > 0) {
            variables.filter = queryParams;
        }

        this.ws?.send(JSON.stringify({
            type: 'start',
            id: id,
            payload: {
                data: JSON.stringify({
                    query,
                    variables,
                }),
                extensions: {
                    authorization: {
                        Authorization: token,
                        host: this.apiHost,
                    }
                }
            },
        }));
    }

    private unsubscribe(id: string) {
        // Send a stop message to terminate the subscription
        this.ws?.send(JSON.stringify({
            type: 'stop',
            id: id // Use the same id from the subscription start message
        }));
    }

    private scheduleReconnect() {
        if (this.reconnectTimeout) {
            return;
        }
        const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts);
        this.reconnectAttempts++;
        this.reconnectTimeout = window.setTimeout(() => {
            this.reconnectTimeout = null;
            this.start();
        }, delay);
    }

    // Public method to start the WebSocket connection
    public async start() {
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        if (this.isConnecting) {
            return;
        }
        this.ws?.close();
        this.isConnecting = true;
        try {
            this.isConnectionAcknowledged = false;
            await this.connect();
        } catch (e) {
            this.scheduleReconnect();
        } finally {
            this.isConnecting = false;
        }
    }
}
